import { useCallback } from 'react';
import {
  UseMutateAsyncFunction,
  UseMutationOptions,
  UseQueryOptions,
  UseQueryResult,
  useMutation,
  useQuery,
  useQueryClient,
} from 'react-query';
import { useDispatch } from 'react-redux';
import { CustomerCompanyAPI } from 'api/customer-company';
import {
  CreateCustomerCompanyPayload,
  CustomerCompany,
  PeopleOptionOrgChart,
  UpdateCustomerCompanyPayload,
} from 'api/customer-company/types';
import { Widget } from 'api/dashboard';
import { WidgetSourceType } from 'api/dashboard/widget';
import { FileGroupSourceType } from 'api/files';
import { NoteSourceType } from 'api/note';
import { Project, ProjectAPI } from 'api/project';
import { StatusUpdate } from 'api/status-updates';
import { ViewApI } from 'api/views';
import { View, ViewType } from 'api/views/types';
import { ApiCustomerCompanyListViewDefinition } from 'components/organisms/customer-portfolio/company-list/saved-views/types';
import { OrgNodeApi } from 'components/organisms/customer-portfolio/org-chart/types';
import { PeopleListUser } from 'components/organisms/customer-portfolio/people-list/types';
import { GENERAL_API_STALE } from 'config/query-config';
import { ACCOUNT_META_DATA_QUERY_KEY } from 'queries/account';
import { useFileGroupsQuery } from 'queries/files';
import {
  AllNotesBySourceKey,
  GetAllNotesQueryOption,
  useGetAllNotesBySource,
} from 'queries/note';
import {
  QueryWidgetsBySourceKey,
  useGetAllWidgetsBySource,
} from 'queries/widgets';
import { added, updated } from 'stores/companies';

type CustomerCompanyQueryKeyType = ['companies', 'customer', number];
const getCustomerCompanyQueryKey = (
  companyId: number,
): CustomerCompanyQueryKeyType => ['companies', 'customer', companyId];

export const getPeopleHierarchyQueryKey = (companyId: number) => [
  'people',
  'hierarchy',
  companyId,
];

export const getPeopleListQueryKey = (companyId: number) => [
  'people',
  'list',
  companyId,
];

export const useGetCustomerCompanyById = (
  companyId: number,
  options: UseQueryOptions<CustomerCompany, unknown, CustomerCompany> = {},
): UseQueryResult<CustomerCompany> =>
  useQuery<CustomerCompany, unknown, CustomerCompany>(
    getCustomerCompanyQueryKey(companyId),
    () => CustomerCompanyAPI.getCompanyById(companyId),
    {
      refetchOnMount: true,
      staleTime: GENERAL_API_STALE,
      ...options,
    },
  );
export const useRefetchCustomerCompanyById = () => {
  const queryClient = useQueryClient();

  return useCallback(
    async (companyId: number) =>
      queryClient.invalidateQueries(getCustomerCompanyQueryKey(companyId)),
    [queryClient],
  );
};

export const useGetWidgetsForCompanies = (
  options: UseQueryOptions<
    Widget[],
    unknown,
    Widget[],
    QueryWidgetsBySourceKey
  > = {},
) => useGetAllWidgetsBySource(WidgetSourceType.Company, options);

export const useGetManagerOfOptionsQuery = (
  userId: number,
  options: UseQueryOptions<
    PeopleOptionOrgChart[],
    unknown,
    PeopleOptionOrgChart[]
  > = {},
): UseQueryResult<PeopleOptionOrgChart[]> =>
  useQuery<PeopleOptionOrgChart[], unknown, PeopleOptionOrgChart[]>(
    ['orgChart', 'managerOf', userId],
    () => CustomerCompanyAPI.getValidManagerOfOptions(userId),
    options,
  );

export const useGetReportsToOptionsQuery = (
  userId: number,
  options: UseQueryOptions<
    PeopleOptionOrgChart[],
    unknown,
    PeopleOptionOrgChart[]
  > = {},
): UseQueryResult<PeopleOptionOrgChart[]> =>
  useQuery<PeopleOptionOrgChart[], unknown, PeopleOptionOrgChart[]>(
    ['orgChart', 'reportsTo', userId],
    () => CustomerCompanyAPI.getValidReportingToOptions(userId),
    options,
  );

export const useGetPeopleHierarchyQuery = (
  companyId: number,
  options: UseQueryOptions<OrgNodeApi[], unknown, OrgNodeApi[]> = {},
): UseQueryResult<OrgNodeApi[]> =>
  useQuery<OrgNodeApi[], unknown, OrgNodeApi[]>(
    ['people', 'hierarchy', companyId],
    () => CustomerCompanyAPI.getPeopleHierarchy(companyId),
    options,
  );

export const useGetAllNotesByCompanyId = (
  companyId: number,
  options: GetAllNotesQueryOption = {},
) =>
  useGetAllNotesBySource(
    {
      sourceId: companyId,
      sourceType: NoteSourceType.Company,
    },
    options,
  );

export const useRefetchAllNotesByCompanyId = () => {
  const queryClient = useQueryClient();
  const refetchCustomerCompany = useRefetchCustomerCompanyById();

  return useCallback(
    async (companyId: number) => {
      await queryClient.invalidateQueries(
        AllNotesBySourceKey({
          sourceId: companyId,
          sourceType: NoteSourceType.Company,
        }),
      );
      await refetchCustomerCompany(companyId);
    },
    [queryClient, refetchCustomerCompany],
  );
};

export const useGetAllFileGroupsByCompanyId = (companyId: number) =>
  useFileGroupsQuery({
    sourceId: companyId,
    sourceType: FileGroupSourceType.Company,
  });

export const useRefetchAllFileGroupsByCompanyId = (companyId: number) => {
  const { refetch } = useFileGroupsQuery({
    sourceId: companyId,
    sourceType: FileGroupSourceType.Company,
  });

  return useCallback(async () => {
    await refetch();
  }, [refetch]);
};

export const useCompanyPeopleListQuery = (
  companyId: number,
  options: UseQueryOptions<PeopleListUser[], unknown, PeopleListUser[]> = {},
): UseQueryResult<PeopleListUser[]> => {
  return useQuery<PeopleListUser[], unknown, PeopleListUser[]>(
    getPeopleListQueryKey(companyId),
    () => CustomerCompanyAPI.getCompanyPeopleList(companyId),
    options,
  );
};
export const getStatusUpdatesByCompanyKey = (companyId: number) => [
  'status-updates',
  'by-company-id',
  companyId,
];
export const useGetStatusUpdatesByCompany = (
  companyId: number,
  options: UseQueryOptions<StatusUpdate[], unknown, StatusUpdate[]> = {},
): UseQueryResult<StatusUpdate[], unknown> =>
  useQuery<StatusUpdate[], unknown, StatusUpdate[]>(
    getStatusUpdatesByCompanyKey(companyId),
    () => CustomerCompanyAPI.getStatusUpdatesByCompanyId(companyId),
    options,
  );
export const useRefetchStatusUpdateByCompany = () => {
  const queryClient = useQueryClient();

  return useCallback(
    async (companyId: number) => {
      queryClient.invalidateQueries(getStatusUpdatesByCompanyKey(companyId));
    },
    [queryClient],
  );
};
export const CustomerCompanyListViews = ['customer-company', 'views'];

export const useGetCustomerCompanyListViewsQuery = (
  queryOptions: UseQueryOptions<
    View<ApiCustomerCompanyListViewDefinition>[],
    unknown,
    View<ApiCustomerCompanyListViewDefinition>[],
    typeof CustomerCompanyListViews
  > = {},
) =>
  useQuery(
    CustomerCompanyListViews,
    () =>
      ViewApI.getAllViews<ApiCustomerCompanyListViewDefinition>(
        ViewType.CustomerPorfolioList,
      ),
    queryOptions,
  );
type ProjectsByCompanyIdKeyType = [string, number];
export const getProjectsByCompanyIdKey = (
  companyId: number,
): ProjectsByCompanyIdKeyType => ['projects-by-customer', companyId];

export const useGetProjectsByCompany = (
  companyId: number,
  options: UseQueryOptions<Project[], unknown, Project[]> = {},
) =>
  useQuery<Project[], unknown, Project[]>(
    getProjectsByCompanyIdKey(companyId),
    () => ProjectAPI.getProjectsByCustomer(companyId),
    options,
  );

export const useCreateCompanyMutation = (
  options: UseMutationOptions<
    CustomerCompany,
    unknown,
    CreateCustomerCompanyPayload
  > = {},
) => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  return useMutation<CustomerCompany, unknown, CreateCustomerCompanyPayload>(
    (payload) => CustomerCompanyAPI.createCompany(payload),
    {
      onSuccess: async (company) => {
        dispatch(added(company));
        await queryClient.invalidateQueries(ACCOUNT_META_DATA_QUERY_KEY);
      },
      ...options,
    },
  );
};

export type UpdateCustomerCompanyMutationFunctionType = UseMutateAsyncFunction<
  CustomerCompany,
  unknown,
  UpdateCustomerCompanyVariable
>;
type UpdateCustomerCompanyVariable = {
  companyId: number;
  payload: UpdateCustomerCompanyPayload;
};
export const useUpdateCustomerCompanyMutation = (
  options: UseMutationOptions<
    CustomerCompany,
    unknown,
    UpdateCustomerCompanyVariable
  > = {},
) => {
  const dispatch = useDispatch();
  const refetchCustomerCompany = useRefetchCustomerCompanyById();

  return useMutation<CustomerCompany, unknown, UpdateCustomerCompanyVariable>(
    ({ companyId, payload }) =>
      CustomerCompanyAPI.updateCompany(companyId, payload),
    {
      onSuccess: async (company) => {
        dispatch(updated(company));
        await refetchCustomerCompany(company.companyId);
      },
      ...options,
    },
  );
};
